import React, { createContext, useContext, useMemo } from "react";
import { PermissionOverride } from "./RoleAccessContext";
import { useTeam } from "./TeamContext";

// "My team" pages reuse the admin review and goal pages for a manager's direct
// reports. Inside a TeamScope:
//   - permissions come from the company's manager duties, not the access role
//     (the server checks each action against the reporting line);
//   - list requests ask for the team (axiosInstance tags requests from team
//     pages — see utils/workspace.js);
//   - links stay under the team pages (`basePath`).

const TeamScopeContext = createContext(null);

/** The surrounding team scope, or null outside "My team" pages. */
export const useTeamScope = () => useContext(TeamScopeContext);

/** Where review links point: the team pages, or the admin Performance pages. */
export const usePerformanceBase = () => useTeamScope()?.basePath ?? "/performance";

/** The review list to go back to: Team reviews, or Performance → Reviews. */
export const useReviewsListPath = () => {
  const scope = useTeamScope();
  return scope ? `${scope.basePath}/reviews` : "/performance/my-appraisals";
};

const teamPermissions = (duties) => ({
  ...(duties.appraisals || duties.pips
    ? { performance_management: { view: true, review: true, create: true, edit: true, approve: true } }
    : {}),
  ...(duties.goals
    ? { goals_okrs: { view: true, create: true, edit: true, delete: true } }
    : {}),
});

export function TeamScope({ basePath, children }) {
  const { duties } = useTeam();

  const permissionValue = useMemo(() => {
    const permissions = teamPermissions(duties);
    const can = (feature, action = "view") => {
      const perm = permissions[feature];
      if (!perm?.view) return false;
      return action === "view" || Boolean(perm[action]);
    };
    return { permissions, can, loading: false };
  }, [duties]);

  const scope = useMemo(() => ({ basePath, duties }), [basePath, duties]);

  return (
    <TeamScopeContext.Provider value={scope}>
      <PermissionOverride value={permissionValue}>{children}</PermissionOverride>
    </TeamScopeContext.Provider>
  );
}
